import { useEffect, useRef, useState } from 'react'
import type { DashboardTrendMonth } from '@/api/dashboard'
import { formatCompactValue, formatDealValue } from '@/lib/dealFormat'

export type TrendMetric = 'won' | 'new'

const PLOT_HEIGHT = 180
const TOP_PAD = 20 // room for a cap label over the tallest column
const AXIS_BAND = 22 // month labels — part of the container height, never clipped
const LEFT_AXIS = 52 // y tick labels
const BAR_MAX = 24

/** Width of an element, tracked as it resizes, so the chart draws in real pixels (crisp 11px text). */
function useWidth<T extends HTMLElement>() {
  const ref = useRef<T>(null)
  const [width, setWidth] = useState(0)
  useEffect(() => {
    const el = ref.current
    if (!el) return
    const observer = new ResizeObserver(([entry]) => setWidth(entry.contentRect.width))
    observer.observe(el)
    return () => observer.disconnect()
  }, [])
  return [ref, width] as const
}

/** Round up to 1 / 2 / 2.5 / 5 × 10^k so the axis reads in clean steps. */
function niceCeil(v: number): number {
  if (v <= 0) return 1
  const magnitude = 10 ** Math.floor(Math.log10(v))
  const f = v / magnitude
  return (f <= 1 ? 1 : f <= 2 ? 2 : f <= 2.5 ? 2.5 : f <= 5 ? 5 : 10) * magnitude
}

function monthDate(month: string): Date {
  const [y, m] = month.split('-').map(Number)
  return new Date(y, m - 1, 1)
}
const shortMonth = (month: string) =>
  monthDate(month).toLocaleDateString('en-AU', { month: 'short' })
const longMonth = (month: string) =>
  monthDate(month).toLocaleDateString('en-AU', { month: 'long', year: 'numeric' })

/** A column with a 4px rounded data-end and a square foot on the baseline. */
function columnPath(x: number, y: number, w: number, h: number): string {
  const r = Math.min(4, h, w / 2)
  const base = y + h
  return `M${x},${base} L${x},${y + r} A${r},${r} 0 0 1 ${x + r},${y} L${x + w - r},${y} A${r},${r} 0 0 1 ${x + w},${y + r} L${x + w},${base} Z`
}

/**
 * Six months of won (or new) business as a single-series column chart. One series, so no legend —
 * the card title names it. Only the current month and the peak carry a value label; every month's
 * figure is in the hover/focus tooltip and in the screen-reader table beneath.
 */
export default function TrendChart({
  months,
  metric,
  currency,
}: {
  months: DashboardTrendMonth[]
  metric: TrendMetric
  currency: string
}) {
  const [ref, width] = useWidth<HTMLDivElement>()
  const [active, setActive] = useState<number | null>(null)

  const valueOf = (m: DashboardTrendMonth) => (metric === 'won' ? m.wonValue : m.newDealValue)
  const countOf = (m: DashboardTrendMonth) => (metric === 'won' ? m.wonCount : m.newDealCount)
  const noun = metric === 'won' ? 'won' : 'new'
  const values = months.map(valueOf)
  const peak = Math.max(0, ...values)
  const top = niceCeil(peak)
  const ticks = [0, top / 2, top]

  const plotLeft = LEFT_AXIS
  const plotWidth = Math.max(0, width - plotLeft)
  const band = months.length ? plotWidth / months.length : 0
  const barWidth = Math.min(BAR_MAX, band * 0.5)
  const yOf = (v: number) => TOP_PAD + PLOT_HEIGHT - (v / top) * PLOT_HEIGHT
  const last = months.length - 1
  const peakIndex = values.indexOf(peak)
  // Narrow bands can't hold every month name side by side: label every other one, counting back
  // from the current month so that one is always named. A cap label needs ~52px of its own.
  const stride = band < 44 ? 2 : 1
  const capRoom = 52

  if (peak === 0 && months.every((m) => countOf(m) === 0)) {
    return (
      <p className="text-sm text-gray-400 py-10 text-center">
        No {noun} deals in the last {months.length} months.
      </p>
    )
  }

  const activeMonth = active == null ? null : months[active]

  return (
    <div ref={ref} className="relative" onMouseLeave={() => setActive(null)}>
      {width > 0 && (
        <svg width={width} height={TOP_PAD + PLOT_HEIGHT + AXIS_BAND} role="presentation">
          {/* recessive hairline grid + y ticks */}
          {ticks.map((t) => (
            <g key={t}>
              <line
                x1={plotLeft}
                x2={width}
                y1={yOf(t)}
                y2={yOf(t)}
                className={t === 0 ? 'stroke-gray-200' : 'stroke-gray-100'}
                strokeWidth={1}
              />
              <text
                x={plotLeft - 8}
                y={yOf(t)}
                dy="0.32em"
                textAnchor="end"
                className="fill-gray-400 text-[11px] tabular-nums"
              >
                {formatCompactValue(t, currency)}
              </text>
            </g>
          ))}

          {months.map((m, i) => {
            const v = valueOf(m)
            const h = (v / top) * PLOT_HEIGHT
            const x = plotLeft + i * band + (band - barWidth) / 2
            const labelled =
              v > 0 && (i === last || (i === peakIndex && (last - peakIndex) * band >= capRoom))
            return (
              <g key={m.month}>
                {h > 0 && (
                  <path
                    d={columnPath(x, yOf(v), barWidth, h)}
                    className="fill-brand-primary transition-opacity"
                    opacity={active == null || active === i ? 1 : 0.45}
                  />
                )}
                {labelled && (
                  <text
                    x={x + barWidth / 2}
                    y={yOf(v) - 6}
                    textAnchor="middle"
                    className="fill-brand-dark text-[11px] font-semibold"
                  >
                    {formatCompactValue(v, currency)}
                  </text>
                )}
                {(last - i) % stride === 0 && (
                  <text
                    x={plotLeft + i * band + band / 2}
                    y={TOP_PAD + PLOT_HEIGHT + 16}
                    textAnchor="middle"
                    className={
                      i === last
                        ? 'fill-brand-dark text-[11px] font-medium'
                        : 'fill-gray-400 text-[11px]'
                    }
                  >
                    {shortMonth(m.month)}
                  </text>
                )}
                {/* the whole band is the hit target, and keyboard-focusable with the same readout */}
                <rect
                  x={plotLeft + i * band}
                  y={0}
                  width={band}
                  height={TOP_PAD + PLOT_HEIGHT}
                  fill="transparent"
                  tabIndex={0}
                  aria-label={`${longMonth(m.month)}: ${formatDealValue(v, currency)}, ${countOf(m)} ${noun} deals`}
                  className="cursor-default focus:outline-none"
                  onMouseEnter={() => setActive(i)}
                  onFocus={() => setActive(i)}
                  onBlur={() => setActive(null)}
                />
              </g>
            )
          })}
        </svg>
      )}

      {activeMonth && active != null && (
        <div
          className="pointer-events-none absolute z-10 -translate-x-1/2 -translate-y-full rounded-lg border border-gray-200 bg-white px-2.5 py-1.5 shadow-md whitespace-nowrap"
          style={{
            left: plotLeft + active * band + band / 2,
            top: Math.max(0, yOf(valueOf(activeMonth)) - 24), // above the cap label, not over it
          }}
        >
          <div className="text-sm font-semibold text-brand-darkest">
            {formatDealValue(valueOf(activeMonth), currency)}
          </div>
          <div className="text-[11px] text-gray-500">
            {countOf(activeMonth)} {noun} {countOf(activeMonth) === 1 ? 'deal' : 'deals'} ·{' '}
            {longMonth(activeMonth.month)}
          </div>
        </div>
      )}

      <table className="sr-only">
        <caption>{metric === 'won' ? 'Won business' : 'New business'} by month</caption>
        <thead>
          <tr>
            <th>Month</th>
            <th>Value</th>
            <th>Deals</th>
          </tr>
        </thead>
        <tbody>
          {months.map((m) => (
            <tr key={m.month}>
              <td>{longMonth(m.month)}</td>
              <td>{formatDealValue(valueOf(m), currency)}</td>
              <td>{countOf(m)}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  )
}
